import React, { Component } from "react";
import equal from "fast-deep-equal/es6";
import { observable, reaction } from "mobx";
import { observer } from "mobx-react";

import { definedAndNotNull } from "@formant/core/common/defined";
import { fork } from "@formant/core/common/fork";
import { logger } from "@formant/core/common/logger";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";

import { appStore, authStore, connectedStore, routerStore } from "../stores";
import {
    IGoToTime,
    IPrompt,
    IRequestModuleData,
    ISetModuleTimeRange,
    ISetupModuleMenus,
    isVisualizationMessage,
} from "./CustomViewMessages";
import { DateEdit } from "./Observe/Timeline/DateEdit";
import { PromptPopup } from "./PromptPopup";

function exhaustiveMessageCheck(value: never): void {
    logger.debug(`CustomView: Unrecognized msg.type: ${JSON.stringify(value)}`);
}

export interface ICustomViewProps {
    module?: string;
    src: string;
    className?: string;
    debug?: boolean;
    devices?: IDeviceDetails[];
    onGoToTime?: (msg: IGoToTime) => void;
    onRequestModuleData?: (msg: IRequestModuleData) => void;
    onSetModuleTimeRange?: (msg: ISetModuleTimeRange) => void;
    onSetupModuleMenus?: (msg: ISetupModuleMenus) => void;
}

const moduleDataListeners: Set<
    (channel: string, source: string, msg: any) => void
> = new Set();

@observer
export class CustomView extends Component<ICustomViewProps> {
    @observable
    private prompt?: IPrompt;

    @observable
    private startDate?: Date | undefined;

    @observable
    private endDate?: Date | undefined;

    @observable
    private editingDate: boolean = false;

    @observable
    private time?: Date | undefined;

    private reactionDisposer: (() => void) | null = null;
    private readonly iframeRef: React.RefObject<HTMLIFrameElement>;

    constructor(props: ICustomViewProps) {
        super(props);
        this.iframeRef = React.createRef();
    }

    componentDidMount() {
        definedAndNotNull(this.iframeRef.current).setAttribute(
            "allowvr",
            "true",
        );
        definedAndNotNull(this.iframeRef.current).addEventListener(
            "load",
            this.setupReactions,
        );
        moduleDataListeners.add(this.onModuleData);
        window.addEventListener("message", this.handleMessage);
    }

    componentWillUnmount() {
        this.reactionDisposer?.();
        this.reactionDisposer = null;
        moduleDataListeners.delete(this.onModuleData);
        this.iframeRef.current?.removeEventListener(
            "load",
            this.setupReactions,
        );
        window.removeEventListener("message", this.handleMessage);
    }

    componentDidUpdate(prevProps: ICustomViewProps): void {
        const { devices: previousDevices } = prevProps;
        const { devices: currentDevices } = this.props;

        if (currentDevices) {
            const data = currentDevices.map(_ => ({
                id: _.id,
                name: _.name,
                tags: _.tags,
            }));

            const previousData = previousDevices?.map(_ => ({
                id: _.id,
                name: _.name,
                tags: _.tags,
            }));

            if (!equal(data, previousData)) {
                this.postMessage({
                    type: "overview_devices",
                    data,
                });
            }
        }
    }

    onModuleData = (channel: string, source: string, msg: any) => {
        this.postMessage({
            type: "channel_data",
            channel,
            source,
            data: msg,
        });
    };

    handleMessage = (e: MessageEvent) => {
        const {
            debug,
            devices,
            onGoToTime,
            onRequestModuleData,
            onSetModuleTimeRange,
            onSetupModuleMenus,
        } = this.props;

        // This message has *come* from the iframe that I *own*.
        const shouldHandleMessage =
            this.iframeRef.current &&
            e.source &&
            this.iframeRef.current.contentWindow &&
            e.source === this.iframeRef.current.contentWindow;

        if (!shouldHandleMessage) {
            return;
        }

        const msg = e.data;
        if (!isVisualizationMessage(msg)) {
            return;
        }

        if (debug) {
            logger.debug("Received custom module message", { data: msg });
        }

        switch (msg.type) {
            case "request_module_data": {
                onRequestModuleData?.(msg);
                break;
            }
            case "prompt": {
                this.prompt = msg;
                break;
            }
            case "go_to_time": {
                onGoToTime?.(msg);
                break;
            }
            case "go_to_device": {
                const { navigate, routeTo } = routerStore;
                fork(navigate(routeTo.device({ id: msg.deviceId })));
                break;
            }
            case "show_message": {
                appStore.setSnackbar(msg.message);
                break;
            }
            case "set_module_data_time_range": {
                onSetModuleTimeRange?.(msg);
                break;
            }
            case "setup_module_menus": {
                onSetupModuleMenus?.(msg);
                break;
            }
            case "refresh_auth_token": {
                authStore
                    .token()
                    .then(token => {
                        this.softPostMessage({ type: "auth_token", token });
                    })
                    .catch(_ => {
                        throw _;
                    });
                break;
            }
            case "send_channel_data": {
                moduleDataListeners.forEach(listener => {
                    const isNotSelf = listener !== this.onModuleData;
                    if (isNotSelf) {
                        listener(msg.channel, msg.source, msg.data);
                    }
                });
                break;
            }
            case "request_devices": {
                if (devices) {
                    this.postMessage({
                        type: "overview_devices",
                        data: devices.map(_ => ({
                            id: _.id,
                            name: _.name,
                            tags: _.tags,
                        })),
                    });
                }
                break;
            }
            case "request_date": {
                this.editingDate = true;
                this.endDate = msg.maxTime;
                this.startDate = msg.minTime;
                this.time = msg.time;
                break;
            }
            case "formant_online": {
                this.softPostMessage({
                    type: "formant_online",
                    online: connectedStore.isOnline,
                });
                break;
            }
            default: {
                exhaustiveMessageCheck(msg);
            }
        }
    };

    public postMessage(msg: any, transfer?: Transferable[] | undefined) {
        definedAndNotNull(this.iframeRef.current?.contentWindow).postMessage(
            msg,
            "*",
            transfer,
        );
    }

    private softPostMessage(
        msg: any,
        transfer?: Transferable[] | undefined,
    ): void {
        this.iframeRef.current?.contentWindow?.postMessage(msg, "*", transfer);
    }

    render() {
        const { src, className } = this.props;
        return (
            <>
                <iframe
                    className={className}
                    ref={this.iframeRef}
                    src={src}
                    allow="autoplay; clipboard-read; clipboard-write; camera; microphone; speaker; usb;"
                    allowFullScreen={true}
                />
                {this.prompt && (
                    <PromptPopup
                        schema={this.prompt.schema}
                        onCancel={this.onCancelPrompt}
                        onComplete={this.onCompletePrompt}
                        okText={this.prompt.okText}
                        cancelText={this.prompt.cancelText}
                    />
                )}
                {this.editingDate && (
                    <DateEdit
                        time={this.time || new Date()}
                        maxTime={this.endDate || false}
                        minTime={this.startDate || false}
                        onDateChanged={this.handleDateChange}
                        onClose={this.onStopEditingDate}
                    />
                )}
            </>
        );
    }

    private setupReactions = () => {
        const unsubTokenUpdater = reaction(
            () => authStore.currentToken,
            token => {
                if (token) {
                    this.softPostMessage({ type: "auth_token", token });
                }
            },
        );

        const unsubOnlineDetector = reaction(
            () => connectedStore.isOnline,
            online => this.softPostMessage({ type: "formant_online", online }),
            { fireImmediately: true },
        );

        this.reactionDisposer = () => {
            unsubTokenUpdater();
            unsubOnlineDetector();
        };
    };

    private onCancelPrompt = () => {
        this.postMessage({
            type: "prompt_response",
            promptId: this.prompt?.promptId,
        });
        this.prompt = undefined;
    };

    private onCompletePrompt = (data: string) => {
        this.postMessage({
            type: "prompt_response",
            promptId: this.prompt?.promptId,
            data: JSON.parse(data),
        });
        this.prompt = undefined;
    };

    private handleDateChange = (d: Date) => {
        this.postMessage({
            type: "date_response",
            data: d,
        });
        this.time = undefined;
        this.startDate = undefined;
        this.endDate = undefined;
    };

    private onStopEditingDate = () => {
        this.editingDate = false;
    };
}
